/**
 * WorkflowsCanvasXY — XYFlow (ReactFlow v12) canvas.
 *
 * Replaces: WorkflowsCanvas.tsx (DOM tree + react-zoom-pan-pinch)
 * Preserved: auto-save, version history, node editors, WorkflowNode data model
 *
 * New features: MiniMap, Controls, dot-grid background, smooth bezier edges,
 *               branch labels, "+" edge buttons, AI node health warnings
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  useReactFlow,
  Node as XYNode,
  Edge as XYEdge,
  NodeTypes,
  EdgeTypes,
  ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { WorkflowNode, NodeId } from "../types";
import { getNodeTitle } from "../WorkflowNodeHelpers";
import { buildFlow, FlowWorkflowNodeData } from "./xyFlowUtils";
import {
  WorkflowFlowNode,
  WorkflowEndNode,
  WorkflowEdge as WorkflowFlowEdge,
} from "./CustomFlowNodes";
import useAIConfigs from "@/hooks/useAIConfigs";
import useUsers from "@/hooks/useUsers";
import useTeams from "@/hooks/useTeams";
import useWorkflows from "@/hooks/useWorkflows";
import { ConnectorData } from "../WorkflowsEditor";

const NODE_TYPES: NodeTypes = {
  workflowNode: WorkflowFlowNode,
  workflowEnd: WorkflowEndNode,
};

const EDGE_TYPES: EdgeTypes = {
  workflowEdge: WorkflowFlowEdge,
};

export interface WorkflowsCanvasXYRef {
  fitView: () => void;
}

interface Props {
  nodes: Record<NodeId, WorkflowNode>;
  selectedNodeId: NodeId | null;
  channelPerspective?: string | null;
  onLoad: () => void;
  onSelectNode: (id: NodeId) => void;
  onSelectConnector: (data: ConnectorData) => void;
  onDeleteNode: (id: NodeId) => void;
  onCopyNode: (id: NodeId) => void;
}

function WorkflowsCanvasXYInner(
  {
    nodes,
    selectedNodeId,
    channelPerspective,
    onLoad,
    onSelectNode,
    onSelectConnector,
    onDeleteNode,
    onCopyNode,
  }: Props,
  ref: React.Ref<WorkflowsCanvasXYRef>
) {
  const { fitView } = useReactFlow();
  const { aiConfigurations } = useAIConfigs();
  const { getDisplayNameForUser } = useUsers();
  const { getTeamNameById } = useTeams();
  const { workflows } = useWorkflows();
  const firstLoad = useRef(false);

  useImperativeHandle(ref, () => ({
    fitView: () => fitView({ padding: 0.12, duration: 350 }),
  }));

  // Rebuild the dagre-laid-out graph whenever the node structure changes.
  // We stringify only the structure-relevant fields to avoid spurious rebuilds.
  const structureKey = useMemo(
    () =>
      JSON.stringify(
        Object.values(nodes).map((n) => ({
          id: n.id,
          child: n.child,
          actionType: n.actionType,
          aiConfigId: n.aiConfigId,
          conditionOptions: n.conditionOptions?.map((c) => c.childNodeId),
          options: (n.options ?? []).map((o) => o.childNodeId),
          fallbackChildId: n.fallbackChildId,
          invalidChild: n.invalidChild,
          errorChildId: n.errorChildId,
        }))
      ),
    [nodes]
  );

  const { flowNodes: rawNodes, flowEdges: rawEdges } = useMemo(
    () =>
      buildFlow(
        nodes,
        aiConfigurations,
        getDisplayNameForUser,
        getTeamNameById,
        workflows ?? []
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [structureKey, aiConfigurations]
  );

  const flowNodes: XYNode[] = useMemo(() => {
    return rawNodes.map((n) => {
      if (n.type === "workflowEnd")
        return { ...n, selectable: false, focusable: false };
      const wfNode = nodes[Number(n.id)];
      if (!wfNode) return n;
      return {
        ...n,
        selected: selectedNodeId === wfNode.id,
        data: {
          ...(n.data as FlowWorkflowNodeData),
          nodeTitle: getNodeTitle(wfNode),
          channelPerspective: channelPerspective ?? null,
          onSelect: () => onSelectNode(wfNode.id),
          onDelete: () => onDeleteNode(wfNode.id),
          onCopy: () => onCopyNode(wfNode.id),
        },
      };
    });
  }, [rawNodes, nodes, selectedNodeId, channelPerspective, onSelectNode, onDeleteNode, onCopyNode]);

  const flowEdges: XYEdge[] = useMemo(() => {
    return rawEdges.map((e) => ({
      ...e,
      data: {
        ...(e.data ?? {}),
        onAddBetween: (x: number, y: number) => {
          const d = e.data as {
            parentNodeId?: NodeId;
            parentOptionIndex?: number;
            childNodeId?: NodeId;
          };
          onSelectConnector({
            position: { x, y },
            parentNodeId: d.parentNodeId!,
            parentOptionIndex: d.parentOptionIndex,
            childNodeId: d.childNodeId,
          });
        },
      },
    }));
  }, [rawEdges, onSelectConnector]);

  const handleInit = useCallback(
    (instance: ReactFlowInstance) => {
      if (!firstLoad.current) {
        firstLoad.current = true;
        requestAnimationFrame(() => {
          instance.fitView({ padding: 0.12, duration: 400 });
          onLoad();
        });
      }
    },
    [onLoad]
  );

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: XYNode) => {
      if (node.type === "workflowEnd") return;
      onSelectNode(Number(node.id));
    },
    [onSelectNode]
  );

  const miniMapNodeColor = useCallback(
    (n: XYNode) => {
      if (n.type === "workflowEnd") return "#cbd5e1";
      const wfNode = nodes[Number(n.id)];
      if (!wfNode) return "#a78bfa";
      if (wfNode.type === "trigger") return "#3b82f6";
      if (wfNode.actionType === "condition") return "#22c55e";
      if (wfNode.actionType === "workflowSection") return "#f59e0b";
      if (
        wfNode.actionType === "transferToAI" ||
        wfNode.actionType?.endsWith("AI")
      )
        return "#8b5cf6";
      if (wfNode.actionType === "transferToAgents") return "#06b6d4";
      return "#a78bfa";
    },
    [nodes]
  );

  return (
    <div className="relative w-full h-full">
      {channelPerspective && (
        <div
          className="absolute top-4 left-1/2 z-10 -translate-x-1/2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center gap-2 shadow-sm pointer-events-none"
          style={{ zIndex: 5 }}
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          Viewing from{" "}
          <strong className="ml-0.5">{channelPerspective}</strong> perspective
        </div>
      )}

      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onNodeClick={onNodeClick}
        onInit={handleInit}
        fitView
        fitViewOptions={{ padding: 0.12 }}
        minZoom={0.08}
        maxZoom={2.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        proOptions={{ hideAttribution: true }}
        deleteKeyCode={null}
        className="bg-slate-50"
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1.2}
          color="#d1d5db"
        />
        <Controls
          position="bottom-right"
          showInteractive={false}
          className="!border !border-slate-200 !shadow-sm !rounded-xl overflow-hidden !bg-white"
        />
        <MiniMap
          position="bottom-left"
          nodeColor={miniMapNodeColor}
          maskColor="rgba(248,250,252,0.8)"
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
          }}
          zoomable
          pannable
        />
      </ReactFlow>
    </div>
  );
}

const WorkflowsCanvasXYInnerFwd = forwardRef(WorkflowsCanvasXYInner);

export default function WorkflowsCanvasXY(
  props: Props & { forwardedRef?: React.Ref<WorkflowsCanvasXYRef> }
) {
  const { forwardedRef, ...rest } = props;
  return (
    <ReactFlowProvider>
      <WorkflowsCanvasXYInnerFwd ref={forwardedRef} {...rest} />
    </ReactFlowProvider>
  );
}
